
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { X, Calendar as CalendarIcon, Clock } from "lucide-react";
import { format, addDays } from "date-fns";

interface DateTimePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (dateTime: string) => void;
  phoneNumber?: string;
  showPhoneInput?: boolean;
}

const DateTimePicker = ({ isOpen, onClose, onConfirm, phoneNumber = "", showPhoneInput = false }: DateTimePickerProps) => {
  const [selectedDateOption, setSelectedDateOption] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [phone, setPhone] = useState(phoneNumber);

  // Generate next 7 days as options
  const dateOptions = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(new Date(), i + 1); // Start from tomorrow
    const label = i === 0 ? "Tomorrow" : 
                  i === 1 ? "Day after tomorrow" : 
                  format(date, "EEEE, MMM d");
    return {
      value: format(date, "yyyy-MM-dd"),
      label: label,
      fullDate: format(date, "PPPP")
    };
  });

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
    "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
  ];

  const handleConfirm = () => {
    const requiredPhone = showPhoneInput ? phone : phoneNumber;
    
    if (selectedDateOption && selectedTime && requiredPhone) {
      // Fire GTM event for schedule call submission ONLY when confirmed
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({ event: 'cta_schedule_call_submit' });
      }
      
      // Add system message to chat session
      const sessionId = localStorage.getItem('visitor_session_id');
      if (sessionId) {
        import('@/utils/chatSystemMessages').then(({ addSystemMessage }) => {
          const selectedOption = dateOptions.find(option => option.value === selectedDateOption);
          const scheduledDateTime = `${selectedOption?.fullDate} at ${selectedTime}`;
          addSystemMessage(sessionId, `📅 Callback scheduled for ${scheduledDateTime} (Phone: ${requiredPhone})`);
        });
      }
      
      // Get user's timezone
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Find the selected date option details
      const selectedOption = dateOptions.find(option => option.value === selectedDateOption);
      
      if (selectedOption) {
        // Create the confirmation message with timezone
        const confirmationMessage = `✅ Your meeting is scheduled for ${selectedOption.fullDate} at ${selectedTime} in your local time zone: ${timeZone}.`;
        
        onConfirm(confirmationMessage);
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="p-6 w-full max-w-md shadow-2xl border-[#364264]/30" style={{
        backgroundColor: '#364264'
      }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#F4F4F4]">Schedule Your Call</h3>
          <button 
            onClick={onClose}
            className="text-[#CCCCCC] hover:text-[#F4F4F4] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Show phone number or phone input */}
          {showPhoneInput ? (
            <div>
              <label className="block text-sm font-medium text-[#F4F4F4] mb-2">
                Phone Number
              </label>
              <Input
                type="tel"
                placeholder="Enter your phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="border-[#747DA1] focus:border-[#D9992B] focus:ring-[#D9992B] bg-[#2B2F4A] text-[#F4F4F4] placeholder:text-[#747DA1]"
                required
              />
            </div>
          ) : phoneNumber ? (
            <div className="bg-green-50/10 border border-green-500/20 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-green-400 text-sm font-medium">Phone: {phoneNumber}</span>
              </div>
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-[#F4F4F4] mb-2 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-[#D9992B]" />
              Select Date
            </label>
            <select
              value={selectedDateOption}
              onChange={(e) => setSelectedDateOption(e.target.value)}
              className="w-full p-3 border border-[#747DA1] rounded-md bg-[#2B2F4A] text-[#F4F4F4] focus:border-[#D9992B] focus:ring-1 focus:ring-[#D9992B] outline-none"
            >
              <option value="" className="bg-[#2B2F4A] text-[#747DA1]">Choose a date</option>
              {dateOptions.map((option) => (
                <option key={option.value} value={option.value} className="bg-[#2B2F4A] text-[#F4F4F4]">
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#F4F4F4] mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#D9992B]" />
              Select Time
            </label>
            <select
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="w-full p-3 border border-[#747DA1] rounded-md bg-[#2B2F4A] text-[#F4F4F4] focus:border-[#D9992B] focus:ring-1 focus:ring-[#D9992B] outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!selectedDateOption}
            >
              <option value="" className="bg-[#2B2F4A] text-[#747DA1]">Choose a time</option>
              {timeSlots.map((time) => (
                <option key={time} value={time} className="bg-[#2B2F4A] text-[#F4F4F4]">
                  {time}
                </option>
              ))}
            </select>
          </div>

          <div className="pt-4 space-y-2">
            <Button 
              onClick={handleConfirm}
              disabled={!selectedDateOption || !selectedTime || (showPhoneInput && !phone)}
              className="w-full text-white py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800"
            >
              Confirm Schedule
            </Button>
            <Button 
              onClick={onClose}
              className="w-full bg-[#747DA1] hover:bg-[#5f6589] text-white py-3 font-semibold"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DateTimePicker;
